import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft, Save, X, AlertTriangle, Plus, Minus } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'

export const AdjustmentPage: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    materialId: '',
    adjustmentType: 'manual',
    reason: '',
    notes: '',
    newQuantity: '',
    adjustmentQuantity: ''
  })

  // Mock materials with current stock - would come from API
  const materials = [
    { 
      id: '1', 
      name: 'Sterling Silver Wire 20ga', 
      currentStock: 150, 
      unitType: 'inch',
      batches: [
        { id: 'b1', quantity: 100, unitCost: 0.15, purchaseDate: '2024-01-15' },
        { id: 'b2', quantity: 50, unitCost: 0.18, purchaseDate: '2024-02-01' }
      ]
    },
    { 
      id: '2', 
      name: 'Gold Filled Wire 18ga', 
      currentStock: 75, 
      unitType: 'inch',
      batches: [
        { id: 'b3', quantity: 75, unitCost: 0.45, purchaseDate: '2024-01-20' }
      ]
    }
  ]

  const adjustmentTypes = [
    { value: 'manual', label: 'Manual Count Adjustment' },
    { value: 'damage', label: 'Damaged/Lost Material' },
    { value: 'found', label: 'Found Material' },
    { value: 'correction', label: 'Inventory Correction' }
  ]

  const selectedMaterial = materials.find(m => m.id === formData.materialId)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // TODO: Replace with actual API call
      console.log('Adjustment data to submit:', formData)
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Navigate back to inventory list
      navigate('/inventory')
    } catch (error) {
      console.error('Error recording adjustment:', error)
      // TODO: Show error message
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => {
      const newData = { ...prev, [name]: value }
      
      // Calculate adjustment quantity when new quantity is entered
      if (name === 'newQuantity' && selectedMaterial) {
        const newQty = parseFloat(value) || 0
        const currentQty = selectedMaterial.currentStock
        newData.adjustmentQuantity = (newQty - currentQty).toString()
      }
      
      return newData
    })
  }

  const adjustmentQuantity = parseFloat(formData.adjustmentQuantity) || 0
  const isIncrease = adjustmentQuantity > 0
  const isDecrease = adjustmentQuantity < 0

  return (
    <>
      <Helmet>
        <title>Stock Adjustment - Jewelry Inventory Tracker</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/inventory')}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Inventory
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Stock Adjustment
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Adjust inventory quantities for physical counts, damage, or corrections
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Material Selection */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Select Material
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Material */}
              <div>
                <label htmlFor="materialId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Material *
                </label>
                <select
                  id="materialId"
                  name="materialId"
                  required
                  value={formData.materialId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                >
                  <option value="">Select a material</option>
                  {materials.map((material) => (
                    <option key={material.id} value={material.id}>
                      {material.name} (Current: {material.currentStock} {material.unitType})
                    </option>
                  ))}
                </select>
              </div>

              {/* Adjustment Type */}
              <div>
                <label htmlFor="adjustmentType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Adjustment Type *
                </label>
                <select
                  id="adjustmentType"
                  name="adjustmentType"
                  required
                  value={formData.adjustmentType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                >
                  {adjustmentTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Current Stock Info */}
          {selectedMaterial && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Current Stock Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedMaterial.currentStock}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Current Stock ({selectedMaterial.unitType})
                  </div>
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedMaterial.batches.length}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Active Batches
                  </div>
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    ${selectedMaterial.batches.reduce((sum, batch) => sum + (batch.quantity * batch.unitCost), 0).toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Total Value
                  </div>
                </div>
              </div>

              {/* Batch Details */}
              <div>
                <h3 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                  Current Batches (FIFO Order)
                </h3>
                <div className="space-y-2">
                  {selectedMaterial.batches.map((batch, index) => (
                    <div key={batch.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge variant={index === 0 ? 'primary' : 'secondary'}>
                          {index === 0 ? 'Next to Use' : `Batch ${index + 1}`}
                        </Badge>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Purchased: {batch.purchaseDate}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {batch.quantity} {selectedMaterial.unitType}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          ${batch.unitCost} per {selectedMaterial.unitType}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Adjustment Details */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Adjustment Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* New Quantity */}
              <div>
                <label htmlFor="newQuantity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Quantity *
                </label>
                <input
                  type="number"
                  id="newQuantity"
                  name="newQuantity"
                  required
                  step="0.01"
                  min="0"
                  value={formData.newQuantity}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  placeholder={selectedMaterial ? `Current: ${selectedMaterial.currentStock}` : '0'}
                />
              </div>

              {/* Adjustment Amount (calculated) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Adjustment Amount
                </label>
                <div className={`px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg flex items-center gap-2 ${
                  isIncrease 
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-600' 
                    : isDecrease
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-600'
                    : 'bg-gray-50 dark:bg-gray-700'
                }`}>
                  {isIncrease ? (
                    <Plus className="w-4 h-4 text-green-600 dark:text-green-400" />
                  ) : isDecrease ? (
                    <Minus className="w-4 h-4 text-red-600 dark:text-red-400" />
                  ) : null}
                  <span className={`font-medium ${
                    isIncrease 
                      ? 'text-green-600 dark:text-green-400' 
                      : isDecrease
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    {adjustmentQuantity === 0 ? '0' : Math.abs(adjustmentQuantity).toFixed(2)} {selectedMaterial?.unitType || ''}
                  </span>
                </div>
              </div>

              {/* Reason */}
              <div className="md:col-span-2">
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason for Adjustment *
                </label>
                <input
                  type="text"
                  id="reason"
                  name="reason"
                  required
                  value={formData.reason}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  placeholder="e.g., Physical count discrepancy, damaged material, found extra stock"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="mt-6">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Additional Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                rows={3}
                value={formData.notes}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                placeholder="Any additional details about this adjustment..."
              />
            </div>
          </div>

          {/* Warning for Decreases */}
          {isDecrease && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                <div>
                  <h3 className="font-medium text-yellow-800 dark:text-yellow-200">
                    Stock Decrease Warning
                  </h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    This adjustment will decrease your inventory by {Math.abs(adjustmentQuantity).toFixed(2)} {selectedMaterial?.unitType}. 
                    This will affect FIFO cost calculations for future projects. Make sure this adjustment is accurate.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-4 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/inventory')}
              leftIcon={<X className="w-4 h-4" />}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              leftIcon={<Save className="w-4 h-4" />}
              disabled={!formData.materialId || !formData.newQuantity || !formData.reason}
            >
              Record Adjustment
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}